import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IServiceChargeInvoicingService } from "services/ServiceChargeInvoicingService";
import { RootState } from "setup";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

type InvoicePreviewsProps = {
  RunGenerateInvoicePreviews: boolean;
  GeneratingInvoices: boolean;
  Progress: number;
};

const initialState: InvoicePreviewsProps = {
  RunGenerateInvoicePreviews: false,
  GeneratingInvoices: false,
  Progress: 0,
};

export const InvoicesPreviewSlice = createSlice({
  name: "InvoicesPreview",
  initialState,
  reducers: {
    setRunGenerateInvoicePreviews: (state, action) => {
      state.RunGenerateInvoicePreviews = action.payload;
    },
    setGeneratingInvoices: (state, action) => {
      state.GeneratingInvoices = action.payload;
    },
    setProgress: (state, action) => {
      state.Progress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(generateInvoiceModelsBackgroundProcess.pending, (state) => {
      state.GeneratingInvoices = true;
      state.RunGenerateInvoicePreviews = false;
    });
  },
});

export const generateInvoiceModelsBackgroundProcess = createAsyncThunk<void, undefined, { state: RootState }>(
  "InvoicesPreview/generateInvoiceModelsBackgroundProcess",
  async (_, thunkAPI) => {
    const serviceChargeInvoicingService = container.get<IServiceChargeInvoicingService>(
      SERVICE_TYPES.ServiceChargeInvoicingService
    );
    const state = thunkAPI.getState();
    await serviceChargeInvoicingService.generateInvoiceModelsBackgroundProcess(
      state.serviceChargeInvoicing.ServiceChargeInvoice
    );
  }
);

export const getPreviewURL = createAsyncThunk<string | undefined, void, { state: RootState }>(
  "InvoicesPreview/getPreviewURL",
  async (_, thunkAPI) => {
    const serviceChargeInvoicingService = container.get<IServiceChargeInvoicingService>(
      SERVICE_TYPES.ServiceChargeInvoicingService
    );
    const state = thunkAPI.getState();
    const previewURL = await serviceChargeInvoicingService.generateReportPreview(
      state.serviceChargeInvoicing.ServiceChargeInvoice
    );

    return previewURL;
  }
);

export const acceptInvoices = createAsyncThunk<void, undefined, { state: RootState }>(
  "InvoicesPreview/acceptInvoices",
  async () => {
    const serviceChargeInvoicingService = container.get<IServiceChargeInvoicingService>(
      SERVICE_TYPES.ServiceChargeInvoicingService
    );
    return await serviceChargeInvoicingService.acceptInvoices();
  }
);

export const { setGeneratingInvoices, setRunGenerateInvoicePreviews, setProgress } = InvoicesPreviewSlice.actions;

export const selectRunGenerateInvoicePreviews = (state: RootState) =>
  state.serviceChargeInvoicePreviews.RunGenerateInvoicePreviews;
export const selectGeneratingInvoices = (state: RootState) => state.serviceChargeInvoicePreviews.GeneratingInvoices;
export const selectProgress = (state: RootState) => state.serviceChargeInvoicePreviews.Progress;

export default InvoicesPreviewSlice.reducer;
